import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { MapContainer, TileLayer, Marker as LeafletMarker, Popup, useMap, Polygon, Tooltip as LeafletTooltip } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import {
  MapPin,
  List,
  Map as MapIcon,
  Filter,
  Search,
  Building2,
  ChevronRight,
  ChevronDown,
  X,
  Anchor,
  ExternalLink,
  RotateCcw,
  Star,
  Clock,
  FileText,
  Globe,
  Download,
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { useAllMapLeads, useLeadsList, useLeadsByCountry, useAvailableFilterOptions, useLongTermLeads, useTouchedLeads, type MapBounds, type DataSource } from '@/hooks/useLeads'
import { useShortlist, useAddToShortlist, useRemoveFromShortlist, SHORTLIST_STATUS_LABELS } from '@/hooks/useShortlist'
import { usePageHeader } from '@/contexts/PageHeaderContext'
import type { Lead, LeadStatus, LeadType } from '@/types/lead'
import { cn } from '@/lib/utils'

// Status colors and labels
const STATUS_COLORS: Record<LeadStatus, string> = {
  new: '#6366f1',
  contacted: '#8b5cf6',
  qualified: '#06b6d4',
  proposal: '#f59e0b',
  negotiation: '#f97316',
  won: '#22c55e',
  lost: '#ef4444',
}

// Geographic regions with polygon coordinates [lat, lng][] and bounding box for filtering
const REGIONS: Record<string, { name: string; bounds: [number, number, number, number]; polygon: [number, number][] }> = {
  'northern-europe': {
    name: 'Northern Europe',
    bounds: [54, -25, 72, 32],
    polygon: [[54, -25], [54, 5], [56, 10], [55, 15], [58, 20], [60, 32], [72, 32], [72, -25]]
  },
  'western-europe': {
    name: 'Western Europe',
    bounds: [42, -10, 54, 15],
    polygon: [[43, -10], [48, -5], [51, 2], [54, 8], [54, 15], [47, 15], [44, 8], [42, 3], [42, -10]]
  },
  'southern-europe': {
    name: 'Southern Europe',
    bounds: [35, -10, 47, 30],
    polygon: [[36, -10], [37, -6], [43, -2], [44, 5], [47, 15], [45, 20], [42, 30], [35, 28], [35, -10]]
  },
  'eastern-europe': {
    name: 'Eastern Europe',
    bounds: [42, 15, 70, 60],
    polygon: [[42, 15], [45, 22], [48, 25], [55, 22], [60, 30], [70, 40], [70, 60], [50, 60], [45, 40], [42, 30]]
  },
  'northern-america': {
    name: 'Northern America',
    bounds: [25, -170, 72, -50],
    polygon: [[25, -125], [30, -85], [25, -80], [45, -65], [50, -55], [72, -60], [72, -170], [55, -170], [50, -130], [35, -120]]
  },
  'central-america': {
    name: 'Central America',
    bounds: [7, -120, 25, -75],
    polygon: [[7, -82], [10, -85], [15, -92], [20, -105], [25, -118], [25, -100], [22, -87], [18, -87], [15, -83], [10, -75], [7, -77]]
  },
  'south-america': {
    name: 'South America',
    bounds: [-56, -82, 13, -34],
    polygon: [[13, -72], [10, -62], [5, -52], [-5, -35], [-23, -35], [-35, -55], [-56, -70], [-55, -75], [-40, -75], [-20, -82], [0, -80], [10, -75]]
  },
  'caribbean': {
    name: 'Caribbean',
    bounds: [10, -90, 28, -58],
    polygon: [[10, -85], [12, -62], [18, -58], [25, -72], [28, -80], [25, -88], [20, -90], [15, -88]]
  },
  'northern-africa': {
    name: 'Northern Africa',
    bounds: [15, -18, 38, 35],
    polygon: [[15, -18], [20, -18], [25, -12], [32, -8], [38, 0], [38, 35], [22, 35], [20, 25], [15, 20]]
  },
  'western-africa': {
    name: 'Western Africa',
    bounds: [-5, -25, 20, 15],
    polygon: [[-5, -10], [5, -18], [15, -25], [20, -18], [20, 0], [15, 15], [5, 12], [0, 8], [-5, 5]]
  },
  'eastern-africa': {
    name: 'Eastern Africa',
    bounds: [-12, 25, 18, 52],
    polygon: [[-12, 35], [-5, 40], [5, 42], [12, 52], [18, 50], [18, 32], [10, 28], [0, 30], [-8, 28]]
  },
  'southern-africa': {
    name: 'Southern Africa',
    bounds: [-35, 10, -5, 42],
    polygon: [[-35, 18], [-34, 28], [-28, 32], [-20, 35], [-10, 42], [-5, 40], [-5, 12], [-15, 12], [-28, 15], [-35, 18]]
  },
  'western-asia': {
    name: 'Western Asia',
    bounds: [12, 25, 45, 65],
    polygon: [[12, 42], [15, 52], [22, 58], [30, 62], [40, 65], [45, 55], [42, 40], [38, 28], [32, 25], [25, 35], [18, 38]]
  },
  'southern-asia': {
    name: 'Southern Asia',
    bounds: [5, 60, 40, 100],
    polygon: [[5, 72], [8, 77], [10, 92], [22, 100], [35, 95], [40, 75], [35, 62], [28, 60], [20, 68], [10, 72]]
  },
  'eastern-asia': {
    name: 'Eastern Asia',
    bounds: [18, 100, 55, 150],
    polygon: [[18, 105], [22, 120], [30, 122], [35, 130], [40, 145], [50, 150], [55, 140], [55, 110], [45, 100], [35, 102], [25, 100]]
  },
  'south-eastern-asia': {
    name: 'South Eastern Asia',
    bounds: [-12, 92, 28, 140],
    polygon: [[-12, 105], [-8, 115], [-5, 130], [0, 140], [10, 125], [20, 120], [28, 100], [20, 92], [8, 95], [0, 100]]
  },
  'australia-nz': {
    name: 'Australia & New Zealand',
    bounds: [-50, 110, -10, 180],
    polygon: [[-50, 165], [-45, 175], [-35, 180], [-30, 175], [-10, 155], [-12, 130], [-20, 112], [-32, 114], [-38, 140], [-45, 150]]
  },
  'melanesia': {
    name: 'Melanesia',
    bounds: [-25, 140, 0, 180],
    polygon: [[-25, 150], [-20, 170], [-10, 180], [0, 170], [0, 145], [-8, 140], [-15, 142], [-22, 145]]
  },
}

// Fix for default marker icons in Leaflet with bundlers
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

// Custom shipyard marker icon
const createShipyardIcon = (status: LeadStatus, isShortlisted: boolean = false) => {
  const color = STATUS_COLORS[status] || '#3b82f6'

  if (isShortlisted) {
    return L.divIcon({
      className: 'custom-shipyard-marker',
      html: `
        <div style="
          width: 36px;
          height: 36px;
          background: ${color};
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 3.5px solid #f59e0b;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 0 2px #fde68a, 0 0 12px rgba(245, 158, 11, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg style="transform: rotate(45deg); width: 16px; height: 16px; color: white;" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
        </div>
      `,
      iconSize: [36, 36],
      iconAnchor: [18, 36],
      popupAnchor: [0, -36],
    })
  }

  return L.divIcon({
    className: 'custom-shipyard-marker',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        background: ${color};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg style="transform: rotate(45deg); width: 16px; height: 16px; color: white;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="5" r="3"/>
          <line x1="12" y1="22" x2="12" y2="8"/>
          <path d="M5 12H2a10 10 0 0 0 20 0h-3"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  })
}

// Country center coordinates for initial view (keyed by country name)
const COUNTRY_CENTERS: Record<string, { center: [number, number]; zoom: number }> = {
  'Netherlands': { center: [52.1326, 5.2913], zoom: 8 },
  'Norway': { center: [62.0, 10.0], zoom: 5 },
  'Denmark': { center: [56.2639, 9.5018], zoom: 7 },
  'Belgium': { center: [50.5039, 4.4699], zoom: 8 },
  'Bulgaria': { center: [42.7339, 25.4858], zoom: 7 },
  'United Kingdom': { center: [54.0, -2.0], zoom: 6 },
  'Germany': { center: [51.1657, 10.4515], zoom: 6 },
  'France': { center: [46.6034, 1.8883], zoom: 6 },
  'Italy': { center: [41.8719, 12.5674], zoom: 6 },
  'Spain': { center: [40.4168, -3.7038], zoom: 6 },
  'United States': { center: [39.8283, -98.5795], zoom: 4 },
  'China': { center: [35.8617, 104.1954], zoom: 4 },
  'Japan': { center: [36.2048, 138.2529], zoom: 5 },
  'Australia': { center: [-25.2744, 133.7751], zoom: 4 },
  'Canada': { center: [56.1304, -106.3468], zoom: 4 },
  'Brazil': { center: [-14.235, -51.9253], zoom: 4 },
  'India': { center: [20.5937, 78.9629], zoom: 5 },
  'South Korea': { center: [35.9078, 127.7669], zoom: 7 },
  'Singapore': { center: [1.3521, 103.8198], zoom: 11 },
  'Turkey': { center: [38.9637, 35.2433], zoom: 6 },
  'Greece': { center: [39.0742, 21.8243], zoom: 7 },
  'Poland': { center: [51.9194, 19.1451], zoom: 6 },
  'Sweden': { center: [60.1282, 18.6435], zoom: 5 },
  'Finland': { center: [61.9241, 25.7482], zoom: 5 },
}

// Europe center for initial view
const EUROPE_CENTER: [number, number] = [54.0, 10.0]
const EUROPE_ZOOM = 4

// Country name to ISO code mapping for common countries
const COUNTRY_TO_CODE: Record<string, string> = {
  'Netherlands': 'NL',
  'Norway': 'NO',
  'Denmark': 'DK',
  'Belgium': 'BE',
  'Bulgaria': 'BG',
  'United Kingdom': 'GB',
  'Germany': 'DE',
  'France': 'FR',
  'Italy': 'IT',
  'Spain': 'ES',
  'United States': 'US',
  'China': 'CN',
  'Japan': 'JP',
  'Australia': 'AU',
  'Canada': 'CA',
  'Brazil': 'BR',
  'India': 'IN',
  'South Korea': 'KR',
  'Singapore': 'SG',
  'Turkey': 'TR',
  'Greece': 'GR',
  'Poland': 'PL',
  'Sweden': 'SE',
  'Finland': 'FI',
  'Portugal': 'PT',
  'Croatia': 'HR',
  'Romania': 'RO',
  'Ireland': 'IE',
  'Austria': 'AT',
  'Switzerland': 'CH',
  'Russia': 'RU',
  'Ukraine': 'UA',
  'Indonesia': 'ID',
  'Malaysia': 'MY',
  'Thailand': 'TH',
  'Vietnam': 'VN',
  'Philippines': 'PH',
  'Taiwan': 'TW',
  'Mexico': 'MX',
  'Argentina': 'AR',
  'Chile': 'CL',
  'Colombia': 'CO',
  'South Africa': 'ZA',
  'Egypt': 'EG',
  'United Arab Emirates': 'AE',
  'Saudi Arabia': 'SA',
  'Qatar': 'QA',
  'Kuwait': 'KW',
  'Bahrain': 'BH',
  'Oman': 'OM',
  'New Zealand': 'NZ',
  'Czech Republic': 'CZ',
  'Hungary': 'HU',
  'Slovakia': 'SK',
  'Slovenia': 'SI',
  'Estonia': 'EE',
  'Latvia': 'LV',
  'Lithuania': 'LT',
  'Malta': 'MT',
  'Cyprus': 'CY',
  'Iceland': 'IS',
  'Luxembourg': 'LU',
  'Monaco': 'MC',
}

const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
}

const TYPE_LABELS: Record<LeadType, string> = {
  repair: 'Repair',
  newbuild: 'New Build',
  scrapping: 'Scrapping',
}

// Employee range options - must match database values exactly
const EMPLOYEE_RANGES = [
  '1-5',
  '6-10',
  '11-20',
  '21-50',
  '51-100',
  '101-250',
  '251-500',
  '501-1,000',
  '1,001-2,000',
  '2,001-5,000',
  '5,001-10,000',
  '10,000+',
]

// Revenue range options - must match database values exactly
const REVENUE_RANGES = [
  '$0M-$1M',
  '$1M-$5M',
  '$5M-$10M',
  '$10M-$25M',
  '$25M-$50M',
  '$50M-$100M',
  '$100M-$250M',
  '$250M-$500M',
  '$500M-$1B',
  '$1B-$5B',
  '$5B+',
]

const PIE_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
]

// Map controller to handle view changes and report bounds
function MapController({
  selectedCountryCode,
  selectedRegion,
  showRegionsMode,
  onBoundsChange,
  countryLeads,
}: {
  selectedCountryCode: string | null
  selectedRegion: string | null
  showRegionsMode: boolean
  onBoundsChange: (bounds: MapBounds) => void
  countryLeads: { latitude: number | null; longitude: number | null }[]
}) {
  const map = useMap()

  // Report bounds when map moves
  useEffect(() => {
    const updateBounds = () => {
      const bounds = map.getBounds()
      onBoundsChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      })
    }

    // Initial update
    updateBounds()

    // Listen for map movement (debounced via moveend)
    map.on('moveend', updateBounds)

    return () => {
      map.off('moveend', updateBounds)
    }
  }, [map, onBoundsChange])

  // Handle country selection zoom - only fly if country actually changed
  const prevCountryRef = useRef<string | null>(null)
  useEffect(() => {
    // Skip if country hasn't changed
    if (prevCountryRef.current === selectedCountryCode) return
    prevCountryRef.current = selectedCountryCode

    if (selectedCountryCode) {
      // First try predefined centers
      if (COUNTRY_CENTERS[selectedCountryCode]) {
        const { center, zoom } = COUNTRY_CENTERS[selectedCountryCode]
        map.flyTo(center, zoom, { duration: 1 })
      } else {
        // Calculate bounds from leads with coordinates
        const validLeads = countryLeads.filter(l => l.latitude != null && l.longitude != null)
        if (validLeads.length > 0) {
          const lats = validLeads.map(l => l.latitude!)
          const lngs = validLeads.map(l => l.longitude!)
          const bounds = L.latLngBounds(
            [Math.min(...lats), Math.min(...lngs)],
            [Math.max(...lats), Math.max(...lngs)]
          )
          map.flyToBounds(bounds, { padding: [50, 50], duration: 1, maxZoom: 10 })
        }
      }
    } else if (selectedCountryCode === null && prevCountryRef.current !== null) {
      // Only fly to Europe when deselecting a country, not on initial load
      map.flyTo(EUROPE_CENTER, EUROPE_ZOOM, { duration: 1 })
    }
  }, [selectedCountryCode, countryLeads, map])

  // Handle region selection zoom
  const prevRegionRef = useRef<string | null>(null)
  useEffect(() => {
    // Skip if region hasn't changed
    if (prevRegionRef.current === selectedRegion) return
    prevRegionRef.current = selectedRegion

    if (selectedRegion && REGIONS[selectedRegion]) {
      const [south, west, north, east] = REGIONS[selectedRegion].bounds
      const bounds = L.latLngBounds(
        [south, west],
        [north, east]
      )
      map.flyToBounds(bounds, { padding: [20, 20], duration: 1 })
    } else if (selectedRegion === null && prevRegionRef.current !== null) {
      // Fly back to Europe when deselecting region
      map.flyTo(EUROPE_CENTER, EUROPE_ZOOM, { duration: 1 })
    }
  }, [selectedRegion, map])

  // Handle region selection mode - zoom out to world view
  const prevShowRegionsRef = useRef<boolean>(false)
  useEffect(() => {
    if (showRegionsMode && !prevShowRegionsRef.current) {
      // Entering regions mode - zoom out to world view
      map.flyTo([30, 0], 2, { duration: 0.8 })
    } else if (!showRegionsMode && prevShowRegionsRef.current && !selectedRegion) {
      // Exiting regions mode without selecting - go back to Europe
      map.flyTo(EUROPE_CENTER, EUROPE_ZOOM, { duration: 0.8 })
    }
    prevShowRegionsRef.current = showRegionsMode
  }, [showRegionsMode, selectedRegion, map])

  return null
}

// Cluster icon creation
const createClusterCustomIcon = (cluster: { getChildCount: () => number }) => {
  const count = cluster.getChildCount()
  let sizeClass = 'w-10 h-10 text-sm'

  if (count >= 10) {
    sizeClass = 'w-12 h-12 text-base'
  }
  if (count >= 30) {
    sizeClass = 'w-14 h-14 text-lg'
  }

  return L.divIcon({
    html: `<div class="flex items-center justify-center ${sizeClass} rounded-full bg-primary-600 text-white font-bold shadow-lg border-3 border-white">${count}</div>`,
    className: 'custom-marker-cluster',
    iconSize: L.point(40, 40, true),
  })
}

// Point-in-polygon algorithm (ray casting)
function isPointInPolygon(lat: number, lng: number, polygon: [number, number][]): boolean {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [yi, xi] = polygon[i]
    const [yj, xj] = polygon[j]

    if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside
    }
  }
  return inside
}

export function LeadsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { setHeaderContent } = usePageHeader()

  // Read state from URL params (keeping only server-side filterable params)
  const view = (searchParams.get('view') as 'map' | 'list') || 'map'
  const selectedCountryCode = searchParams.get('country') || ''
  const searchQuery = searchParams.get('q') || ''
  const statusFilter = searchParams.get('status') || ''
  const typeFilter = searchParams.get('type') || ''
  const employeeFilter = searchParams.get('employees') || ''
  const revenueFilter = searchParams.get('revenue') || ''
  const dataSourceFilter = (searchParams.get('source') as DataSource) || ''
  const shortlistedOnly = searchParams.get('shortlisted') === 'true'
  const longTermOnly = searchParams.get('longterm') === 'true'
  const touchedOnly = searchParams.get('touched') === 'true'
  const regionFilter = searchParams.get('region') || ''

  const [showFilters, setShowFilters] = useState(false)
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null)
  const [showRegions, setShowRegions] = useState(false)

  // Memoized callback to prevent MapController re-renders
  // Only update if bounds changed significantly to prevent infinite loops
  const handleBoundsChange = useCallback((bounds: MapBounds) => {
    setMapBounds(prev => {
      if (prev) {
        // Check if bounds changed significantly (more than 0.001 degrees)
        const threshold = 0.001
        const unchanged =
          Math.abs(prev.north - bounds.north) < threshold &&
          Math.abs(prev.south - bounds.south) < threshold &&
          Math.abs(prev.east - bounds.east) < threshold &&
          Math.abs(prev.west - bounds.west) < threshold
        if (unchanged) return prev
      }
      return bounds
    })
  }, [])

  // Fetch country stats (lightweight - just counts)
  const { data: countryStats = [], isLoading: loadingCountries } = useLeadsByCountry()

  // Fetch ALL leads with coordinates once - then filter client-side
  const { data: allMapLeads = [], isLoading: loadingMapLeads } = useAllMapLeads()

  // Touched leads (have notes, activities, or contacts) - must be before mapLeads useMemo
  const { data: touchedIds = new Set<string>() } = useTouchedLeads()

  // Client-side filtering for map leads
  const mapLeads = useMemo(() => {
    let leads = allMapLeads

    // Apply bounds filter (only if no country/shortlist/longterm filter)
    if (mapBounds && !selectedCountryCode && !shortlistedOnly && !longTermOnly) {
      leads = leads.filter(lead =>
        lead.latitude !== null &&
        lead.longitude !== null &&
        lead.latitude >= mapBounds.south &&
        lead.latitude <= mapBounds.north &&
        lead.longitude >= mapBounds.west &&
        lead.longitude <= mapBounds.east
      )
    }

    // Apply country filter
    if (selectedCountryCode) {
      leads = leads.filter(lead => lead.country === selectedCountryCode)
    }

    // Apply status filter
    if (statusFilter) {
      leads = leads.filter(lead => lead.status === statusFilter)
    }

    // Apply type filter
    if (typeFilter) {
      leads = leads.filter(lead => lead.type === typeFilter)
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      leads = leads.filter(lead => lead.name?.toLowerCase().includes(query))
    }

    // Apply employee range filter
    if (employeeFilter) {
      leads = leads.filter(lead => lead.employee_range === employeeFilter)
    }

    // Apply revenue range filter
    if (revenueFilter) {
      leads = leads.filter(lead => lead.revenue_range === revenueFilter)
    }

    // Apply region filter (by polygon)
    if (regionFilter && REGIONS[regionFilter]) {
      const polygon = REGIONS[regionFilter].polygon
      leads = leads.filter(lead =>
        lead.latitude !== null &&
        lead.longitude !== null &&
        isPointInPolygon(lead.latitude, lead.longitude, polygon)
      )
    }

    // Apply touched filter
    if (touchedOnly) {
      leads = leads.filter(lead => touchedIds.has(lead.id))
    }

    return leads
  }, [allMapLeads, mapBounds, selectedCountryCode, shortlistedOnly, longTermOnly, statusFilter, typeFilter, searchQuery, employeeFilter, revenueFilter, regionFilter, touchedOnly, touchedIds])

  // Get leads for selected country (used for fitBounds calculation)
  const countryLeads = useMemo(() => {
    if (!selectedCountryCode) return []
    return allMapLeads.filter(lead => lead.country === selectedCountryCode)
  }, [allMapLeads, selectedCountryCode])

  // Server-side filters for list view only (map uses client-side filtering now)
  const listFilters = useMemo(() => ({
    status: statusFilter ? [statusFilter as LeadStatus] : undefined,
    type: typeFilter ? [typeFilter as LeadType] : undefined,
    country: selectedCountryCode ? [selectedCountryCode] : undefined,
    search: searchQuery || undefined,
    employeeRange: employeeFilter || undefined,
    revenueRange: revenueFilter || undefined,
    dataSource: dataSourceFilter || undefined,
  }), [statusFilter, typeFilter, selectedCountryCode, searchQuery, employeeFilter, revenueFilter, dataSourceFilter])

  // Fetch leads for list view (paginated)
  const { data: listData } = useLeadsList({
    ...listFilters,
    page: 0,
    pageSize: 200,
  })

  const listLeads = listData?.leads || []
  const totalCount = listData?.totalCount || 0

  // Shortlist
  const { data: shortlist = [] } = useShortlist()
  const addToShortlist = useAddToShortlist()
  const removeFromShortlist = useRemoveFromShortlist()

  // Long term leads
  const { data: longTermIds = new Set<string>() } = useLongTermLeads()

  const shortlistedIds = useMemo(() =>
    new Set(shortlist.map(item => item.lead_id)),
    [shortlist]
  )

  // Export shortlisted leads to Excel
  const exportShortlistToExcel = useCallback(() => {
    const shortlistedLeads = shortlist
      .filter(item => item.lead != null)
      .map(item => {
        const lead = item.lead!
        return {
          'Company Name': lead.name || '',
          'Status': STATUS_LABELS[lead.status] || lead.status,
          'Shortlist Status': SHORTLIST_STATUS_LABELS[item.status] || item.status,
          'Country': lead.country || '',
          'City': lead.city || '',
          'Website': lead.website || '',
          'Email': lead.email || '',
          'Phone': lead.phone || '',
          'Industry': lead.industry || '',
          'Employees': lead.employee_range || '',
          'Revenue': lead.revenue_range || '',
          'Services': lead.services?.filter(Boolean).join(', ') || '',
          'Priority': item.priority ?? '',
          'Notes': item.notes || '',
          'Next Action': item.next_action || '',
          'Next Action Date': item.next_action_date || '',
          'Added': item.created_at ? new Date(item.created_at).toLocaleDateString() : '',
        }
      })

    const ws = XLSX.utils.json_to_sheet(shortlistedLeads)

    // Auto-size columns
    const colWidths = Object.keys(shortlistedLeads[0] || {}).map(key => ({
      wch: Math.max(
        key.length,
        ...shortlistedLeads.map(row => String(row[key as keyof typeof row] ?? '').length)
      )
    }))
    ws['!cols'] = colWidths

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Shortlist')
    XLSX.writeFile(wb, `shortlist_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }, [shortlist])

  // Filter country stats based on active region/shortlist/longterm/touched filters
  const filteredCountryStats = useMemo(() => {
    if (!regionFilter && !shortlistedOnly && !longTermOnly && !touchedOnly) {
      return countryStats
    }

    let relevantLeads: Lead[]

    if (shortlistedOnly) {
      relevantLeads = shortlist
        .map(item => item.lead)
        .filter((lead): lead is Lead => lead !== null && lead !== undefined)
    } else {
      relevantLeads = allMapLeads
    }

    if (regionFilter && REGIONS[regionFilter]) {
      const polygon = REGIONS[regionFilter].polygon
      relevantLeads = relevantLeads.filter(lead =>
        lead.latitude !== null &&
        lead.longitude !== null &&
        isPointInPolygon(lead.latitude, lead.longitude, polygon)
      )
    }

    if (longTermOnly) {
      relevantLeads = relevantLeads.filter(lead => lead.long_term || longTermIds.has(lead.id))
    }

    if (touchedOnly) {
      relevantLeads = relevantLeads.filter(lead => touchedIds.has(lead.id))
    }

    const countryCounts = new Map<string, number>()
    relevantLeads.forEach(lead => {
      if (lead.country) {
        countryCounts.set(lead.country, (countryCounts.get(lead.country) || 0) + 1)
      }
    })

    return countryStats
      .filter(cs => countryCounts.has(cs.country))
      .map(cs => ({
        ...cs,
        count: countryCounts.get(cs.country) || cs.count,
      }))
      .sort((a, b) => b.count - a.count)
  }, [countryStats, allMapLeads, shortlist, regionFilter, shortlistedOnly, longTermOnly, longTermIds, touchedOnly, touchedIds])

  const toggleShortlist = async (e: React.MouseEvent, leadId: string) => {
    e.stopPropagation()
    if (shortlistedIds.has(leadId)) {
      await removeFromShortlist.mutateAsync(leadId)
    } else {
      await addToShortlist.mutateAsync({ lead_id: leadId })
    }
  }

  // Get available filter options based on selected country
  const { data: filterOptions } = useAvailableFilterOptions(selectedCountryCode || undefined)

  // Filter the range options to only show available ones
  const availableEmployeeRanges = useMemo(() => {
    if (!filterOptions?.employeeRanges) return EMPLOYEE_RANGES
    return EMPLOYEE_RANGES.filter(range => filterOptions.employeeRanges.includes(range))
  }, [filterOptions?.employeeRanges])

  const availableRevenueRanges = useMemo(() => {
    if (!filterOptions?.revenueRanges) return REVENUE_RANGES
    return REVENUE_RANGES.filter(range => filterOptions.revenueRanges.includes(range))
  }, [filterOptions?.revenueRanges])

  // Clear filters if they're no longer available for the selected country
  useEffect(() => {
    if (filterOptions) {
      const updates: Record<string, string | null> = {}
      if (employeeFilter && !availableEmployeeRanges.includes(employeeFilter)) {
        updates.employees = null
      }
      if (revenueFilter && !availableRevenueRanges.includes(revenueFilter)) {
        updates.revenue = null
      }
      if (Object.keys(updates).length > 0) {
        const params = new URLSearchParams(searchParams)
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null) params.delete(key)
        })
        setSearchParams(params, { replace: true })
      }
    }
  }, [filterOptions, availableEmployeeRanges, availableRevenueRanges])

  // Derive selectedCountry from countryStats (now using country name, not code)
  const selectedCountry = useMemo(() => {
    if (!selectedCountryCode || countryStats.length === 0) return null
    return countryStats.find(c => c.country === selectedCountryCode) || null
  }, [selectedCountryCode, countryStats])

  // Helper to update URL params (replace: true to avoid creating history entries for each filter change)
  const updateParams = (updates: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams)
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === 'map') {
        params.delete(key)
      } else {
        params.set(key, value)
      }
    })
    setSearchParams(params, { replace: true })
  }

  // Use server-fetched data - map view uses mapLeads, list view uses listLeads
  // When shortlist filter is enabled, use leads from shortlist data directly (more reliable)
  const filteredLeads = useMemo(() => {
    // If shortlist filter is enabled, use leads from shortlist data
    if (shortlistedOnly) {
      // Extract leads from shortlist items (shortlist already has lead data via join)
      const shortlistedLeads = shortlist
        .map(item => item.lead)
        .filter((lead): lead is Lead => lead !== null && lead !== undefined)

      // For map view, only include leads with coordinates
      let leads = view === 'map'
        ? shortlistedLeads.filter(lead => lead.latitude != null && lead.longitude != null)
        : shortlistedLeads

      // Apply other filters
      if (selectedCountryCode) {
        leads = leads.filter(lead => lead.country === selectedCountryCode)
      }
      if (statusFilter) {
        leads = leads.filter(lead => lead.status === statusFilter)
      }
      if (typeFilter) {
        leads = leads.filter(lead => lead.type === typeFilter)
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        leads = leads.filter(lead => lead.name?.toLowerCase().includes(query))
      }
      if (employeeFilter) {
        leads = leads.filter(lead => lead.employee_range === employeeFilter)
      }
      if (revenueFilter) {
        leads = leads.filter(lead => lead.revenue_range === revenueFilter)
      }
      if (regionFilter && REGIONS[regionFilter]) {
        const polygon = REGIONS[regionFilter].polygon
        leads = leads.filter(lead =>
          lead.latitude !== null &&
          lead.longitude !== null &&
          isPointInPolygon(lead.latitude, lead.longitude, polygon)
        )
      }
      if (touchedOnly) {
        leads = leads.filter(lead => touchedIds.has(lead.id))
      }

      return leads
    }

    // Normal flow: use mapLeads or listLeads
    // When touchedOnly or longTermOnly is active, use mapLeads (has all leads) even for list view
    // because listLeads only has 200 server-fetched leads that might not include touched/longterm ones
    let leads = (view === 'map' || touchedOnly || longTermOnly) ? mapLeads : listLeads

    if (longTermOnly) {
      leads = leads.filter(lead => lead.long_term || longTermIds.has(lead.id))
    }
    if (touchedOnly) {
      leads = leads.filter(lead => touchedIds.has(lead.id))
    }
    return leads
  }, [view, mapLeads, listLeads, shortlistedOnly, shortlist, longTermOnly, longTermIds, selectedCountryCode, statusFilter, typeFilter, searchQuery, employeeFilter, revenueFilter, regionFilter, touchedOnly, touchedIds])

  // Format revenue for display
  const formatRevenue = (revenue: number) => {
    if (revenue >= 1000000000) return `$${(revenue / 1000000000).toFixed(1)}B`
    if (revenue >= 1000000) return `$${(revenue / 1000000).toFixed(1)}M`
    if (revenue >= 1000) return `$${(revenue / 1000).toFixed(0)}K`
    return `$${revenue}`
  }

  // Prepare pie chart data based on filtered leads - using revenue
  const pieData = useMemo(() => {
    const leadsForPie = view === 'map' ? filteredLeads : []
    const leadsWithRevenue = leadsForPie
      .filter((l) => l.revenue && l.revenue > 0)
      .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
      .slice(0, 8)

    // Calculate total revenue for percentage
    const totalRevenue = leadsWithRevenue.reduce((sum, l) => sum + (l.revenue || 0), 0)

    return leadsWithRevenue.map((lead) => ({
      name: lead.name,
      countryCode: lead.country_code || COUNTRY_TO_CODE[lead.country] || '',
      value: lead.revenue || 0,
      percentage: totalRevenue > 0 ? ((lead.revenue || 0) / totalRevenue) * 100 : 0,
      id: lead.id,
    }))
  }, [filteredLeads, view])

  const getLeadUrl = (lead: Lead) => {
    const params = searchParams.toString()
    return `/admin/leads/${lead.id}${params ? `?${params}` : ''}`
  }

  const handleLeadClick = (lead: Lead, e?: React.MouseEvent) => {
    const url = getLeadUrl(lead)
    // Open in new tab if Ctrl/Cmd+click or middle-click
    if (e && (e.ctrlKey || e.metaKey || e.button === 1)) {
      window.open(url, '_blank')
      return
    }
    navigate(url)
  }

  const openInNewTab = (e: React.MouseEvent, lead: Lead) => {
    e.stopPropagation()
    window.open(getLeadUrl(lead), '_blank')
  }

  const clearFilters = () => {
    const params = new URLSearchParams()
    if (view !== 'map') params.set('view', view)
    setSearchParams(params, { replace: true })
  }

  const setView = (v: 'map' | 'list') => updateParams({ view: v })
  const setSearchQueryValue = (q: string) => updateParams({ q: q || null })
  const setSelectedCountryCode = (code: string) => {
    // Clear region filter when selecting a country
    if (code) {
      setShowRegions(false)
      updateParams({ country: code, region: null })
    } else {
      updateParams({ country: null })
    }
  }
  const setStatusFilter = (status: string) => updateParams({ status: status || null })
  const setTypeFilter = (type: string) => updateParams({ type: type || null })
  const setEmployeeFilter = (range: string) => updateParams({ employees: range || null })
  const setRevenueFilter = (range: string) => updateParams({ revenue: range || null })
  const setDataSourceFilter = (source: string) => updateParams({ source: source || null })
  const setShortlistedOnly = (value: boolean) => updateParams({ shortlisted: value ? 'true' : null })
  const setLongTermOnly = (value: boolean) => updateParams({ longterm: value ? 'true' : null })
  const setTouchedOnly = (value: boolean) => updateParams({ touched: value ? 'true' : null })
  const setRegionFilter = (region: string) => updateParams({ region: region || null })

  const activeFilterCount = [
    searchQuery,
    statusFilter,
    typeFilter,
    selectedCountryCode,
    employeeFilter,
    revenueFilter,
    dataSourceFilter,
    shortlistedOnly,
    longTermOnly,
    regionFilter,
  ].filter(Boolean).length

  const hasActiveFilters = activeFilterCount > 0

  // Set header content for navbar (desktop only)
  useEffect(() => {
    setHeaderContent(
      <div className="flex items-center gap-2 w-full">
        {/* Title & Count */}
        <div className="flex items-center gap-2 mr-auto">
          <h1 className="text-lg font-bold text-secondary-900 whitespace-nowrap">Sales Leads</h1>
          <span className="text-secondary-400">|</span>
          <p className="text-secondary-500 text-sm whitespace-nowrap">
            {view === 'map'
              ? filteredLeads.length > 1000
                ? `1,000 of ${filteredLeads.length.toLocaleString()} shown`
                : `${filteredLeads.length.toLocaleString()} in view`
              : shortlistedOnly
                ? `${filteredLeads.length.toLocaleString()} shortlisted`
                : `${totalCount.toLocaleString()} total`}
          </p>
        </div>

        {/* Search */}
        <div className="relative w-44">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQueryValue(e.target.value)}
            className="w-full pl-9 pr-8 py-1.5 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQueryValue('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Region Toggle - disabled when country is selected */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              if (selectedCountryCode) return
              if (regionFilter) {
                setRegionFilter('')
              } else {
                setShowRegions(!showRegions)
              }
            }}
            disabled={!!selectedCountryCode}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors border',
              selectedCountryCode
                ? 'border-secondary-200 bg-secondary-100 text-secondary-400 cursor-not-allowed'
                : showRegions || regionFilter
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-secondary-300 text-secondary-600 hover:bg-secondary-50'
            )}
          >
            <Globe className="w-4 h-4" />
            {regionFilter ? REGIONS[regionFilter]?.name : (showRegions ? 'Select' : 'Regions')}
          </button>
          {regionFilter && !selectedCountryCode && (
            <button
              onClick={() => setRegionFilter('')}
              className="p-1 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Country Filter */}
        <div className="relative">
          <select
            value={selectedCountryCode}
            onChange={(e) => setSelectedCountryCode(e.target.value)}
            className="appearance-none w-36 pl-3 pr-7 py-1.5 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer truncate"
          >
            <option value="">All Countries</option>
            {filteredCountryStats.map((country, idx) => (
              <option key={`country-${idx}`} value={country.country}>
                {country.country} ({country.count})
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-secondary-400 pointer-events-none" />
        </div>

        {/* Quick Filters */}
        <div className="flex items-center border border-secondary-300 rounded-lg flex-shrink-0">
          <button
            onClick={() => setShortlistedOnly(!shortlistedOnly)}
            className={cn(
              'p-1.5 transition-colors',
              shortlistedOnly ? 'bg-purple-100 text-purple-600' : 'hover:bg-secondary-50 text-secondary-400'
            )}
            title="Shortlist"
          >
            <Star className={cn('w-4 h-4', shortlistedOnly && 'fill-current')} />
          </button>
          <div className="w-px h-5 bg-secondary-200" />
          <button
            onClick={() => setLongTermOnly(!longTermOnly)}
            className={cn(
              'p-1.5 transition-colors',
              longTermOnly ? 'bg-amber-100 text-amber-600' : 'hover:bg-secondary-50 text-secondary-400'
            )}
            title="Long Term"
          >
            <Clock className="w-4 h-4" />
          </button>
          <div className="w-px h-5 bg-secondary-200" />
          <button
            onClick={() => setTouchedOnly(!touchedOnly)}
            className={cn(
              'p-1.5 transition-colors',
              touchedOnly ? 'bg-green-100 text-green-600' : 'hover:bg-secondary-50 text-secondary-400'
            )}
            title="Touched"
          >
            <FileText className="w-4 h-4" />
          </button>
        </div>

        {/* Export Shortlist */}
        {shortlistedOnly && shortlist.length > 0 && (
          <button
            onClick={exportShortlistToExcel}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 text-sm font-medium transition-colors"
            title="Export shortlist to Excel"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        )}

        {/* View Toggle */}
        <div className="flex bg-secondary-100 rounded-lg p-0.5">
          <button
            onClick={() => setView('map')}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium transition-colors',
              view === 'map' ? 'bg-white text-secondary-900 shadow-sm' : 'text-secondary-600'
            )}
          >
            <MapIcon className="w-4 h-4" />
            Map
          </button>
          <button
            onClick={() => setView('list')}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium transition-colors',
              view === 'list' ? 'bg-white text-secondary-900 shadow-sm' : 'text-secondary-600'
            )}
          >
            <List className="w-4 h-4" />
            List
          </button>
        </div>

        {/* Filter Button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-sm font-medium transition-colors',
            showFilters || hasActiveFilters
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-secondary-300 text-secondary-700 hover:bg-secondary-50'
          )}
        >
          <Filter className="w-4 h-4" />
          {hasActiveFilters && (
            <span className="bg-primary-600 text-white text-xs px-1.5 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
    )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view, filteredLeads.length, totalCount, shortlistedOnly, searchQuery,
    regionFilter, showRegions, selectedCountryCode, filteredCountryStats,
    longTermOnly, touchedOnly, showFilters, hasActiveFilters, activeFilterCount,
    exportShortlistToExcel, shortlist.length
  ])

  // Cleanup header on unmount
  useEffect(() => {
    return () => setHeaderContent(null)
  }, [setHeaderContent])

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Mobile Header - Only visible on mobile */}
      <div className="lg:hidden bg-white border-b border-secondary-200 px-4 py-2">
        <div className="flex items-center gap-2">
          <h1 className="text-base font-bold text-secondary-900">Sales Leads</h1>
          <span className="text-secondary-400">|</span>
          <span className="text-secondary-500 text-sm">
            {view === 'map' && filteredLeads.length > 1000
              ? `1K/${filteredLeads.length.toLocaleString()}`
              : filteredLeads.length.toLocaleString()}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {/* View Toggle */}
            <div className="flex bg-secondary-100 rounded-lg p-0.5">
              <button
                onClick={() => setView('map')}
                className={cn(
                  'p-1.5 rounded-md',
                  view === 'map' ? 'bg-white shadow-sm' : ''
                )}
              >
                <MapIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setView('list')}
                className={cn(
                  'p-1.5 rounded-md',
                  view === 'list' ? 'bg-white shadow-sm' : ''
                )}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                'p-1.5 rounded-lg border',
                showFilters || hasActiveFilters
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-secondary-300'
              )}
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="mt-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQueryValue(e.target.value)}
              className="w-full pl-10 pr-10 py-1.5 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQueryValue('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-2 pt-2 border-t border-secondary-200">
            {/* Filters Row */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Country - Mobile only (desktop is in header) */}
              <div className="relative min-w-[140px] sm:hidden">
                <select
                  value={selectedCountryCode}
                  onChange={(e) => setSelectedCountryCode(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">All Countries</option>
                  {filteredCountryStats.map((country, idx) => (
                    <option key={`country-${idx}`} value={country.country}>
                      {country.country} ({country.count})
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
              </div>

              {/* Shortlisted - Mobile only (desktop is in header) */}
              <label className="flex sm:hidden items-center gap-2 px-3 py-2 border border-secondary-300 rounded-lg cursor-pointer hover:bg-secondary-50 transition-colors">
                <input
                  type="checkbox"
                  checked={shortlistedOnly}
                  onChange={(e) => setShortlistedOnly(e.target.checked)}
                  className="w-4 h-4 rounded border-secondary-300 text-purple-600 focus:ring-purple-500"
                />
                <Star className={cn('w-4 h-4', shortlistedOnly ? 'text-purple-500 fill-purple-500' : 'text-secondary-400')} />
                <span className="text-sm font-medium text-secondary-700">Shortlist</span>
              </label>

              {/* Export Shortlist - Mobile */}
              {shortlistedOnly && shortlist.length > 0 && (
                <button
                  onClick={exportShortlistToExcel}
                  className="flex sm:hidden items-center gap-2 px-3 py-2 border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-sm font-medium">Export</span>
                </button>
              )}

              {/* Long Term - Mobile only (desktop is in header) */}
              <label className="flex sm:hidden items-center gap-2 px-3 py-2 border border-secondary-300 rounded-lg cursor-pointer hover:bg-secondary-50 transition-colors">
                <input
                  type="checkbox"
                  checked={longTermOnly}
                  onChange={(e) => setLongTermOnly(e.target.checked)}
                  className="w-4 h-4 rounded border-secondary-300 text-amber-600 focus:ring-amber-500"
                />
                <Clock className={cn('w-4 h-4', longTermOnly ? 'text-amber-500' : 'text-secondary-400')} />
                <span className="text-sm font-medium text-secondary-700 whitespace-nowrap">Long Term</span>
              </label>

              {/* Status */}
              <div className="relative min-w-[130px]">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">All Status</option>
                  {(Object.keys(STATUS_LABELS) as LeadStatus[]).map((status) => (
                    <option key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
              </div>

              {/* Type */}
              <div className="relative min-w-[130px]">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">All Types</option>
                  {(Object.keys(TYPE_LABELS) as LeadType[]).map((type) => (
                    <option key={type} value={type}>
                      {TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
              </div>

              {/* Employees */}
              <div className="relative min-w-[130px]">
                <select
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">All Employees</option>
                  {availableEmployeeRanges.map((range) => (
                    <option key={range} value={range}>
                      {range}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
              </div>

              {/* Revenue */}
              <div className="relative min-w-[130px]">
                <select
                  value={revenueFilter}
                  onChange={(e) => setRevenueFilter(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">All Revenue</option>
                  {availableRevenueRanges.map((range) => (
                    <option key={range} value={range}>
                      {range}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
              </div>

              {/* Data Source */}
              <div className="relative min-w-[140px]">
                <select
                  value={dataSourceFilter}
                  onChange={(e) => setDataSourceFilter(e.target.value)}
                  className="w-full appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">All Sources</option>
                  <option value="trusteddocks">TrustedDocks</option>
                  <option value="other">Manual / Other</option>
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Desktop Filter Panel - Only visible on desktop when showFilters is true */}
      {showFilters && (
        <div className="hidden lg:block bg-white border-b border-secondary-200 px-6 py-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Status */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
              >
                <option value="">All Status</option>
                {(Object.keys(STATUS_LABELS) as LeadStatus[]).map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
            </div>

            {/* Type */}
            <div className="relative">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
              >
                <option value="">All Types</option>
                {(Object.keys(TYPE_LABELS) as LeadType[]).map((type) => (
                  <option key={type} value={type}>
                    {TYPE_LABELS[type]}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
            </div>

            {/* Employees */}
            <div className="relative">
              <select
                value={employeeFilter}
                onChange={(e) => setEmployeeFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
              >
                <option value="">All Employees</option>
                {availableEmployeeRanges.map((range) => (
                  <option key={range} value={range}>
                    {range}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
            </div>

            {/* Revenue */}
            <div className="relative">
              <select
                value={revenueFilter}
                onChange={(e) => setRevenueFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
              >
                <option value="">All Revenue</option>
                {availableRevenueRanges.map((range) => (
                  <option key={range} value={range}>
                    {range}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
            </div>

            {/* Data Source */}
            <div className="relative">
              <select
                value={dataSourceFilter}
                onChange={(e) => setDataSourceFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
              >
                <option value="">All Sources</option>
                <option value="trusteddocks">TrustedDocks</option>
                <option value="other">Manual / Other</option>
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset All
              </button>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {(loadingCountries || loadingMapLeads) ? (
          <div className="flex flex-col items-center justify-center h-full bg-secondary-50">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-primary-200 rounded-full" />
              <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-primary-600 rounded-full animate-spin" />
            </div>
            <p className="mt-4 text-secondary-600 font-medium">Loading shipyards...</p>
            <p className="mt-1 text-sm text-secondary-400">This may take a moment</p>
          </div>
        ) : view === 'map' ? (
          <div className="h-full flex">
            {/* Map */}
            <div className="flex-1 relative bg-secondary-50">
              <MapContainer
                center={EUROPE_CENTER}
                zoom={EUROPE_ZOOM}
                className="w-full h-full"
                style={{ background: '#f1f5f9' }}
                zoomControl={true}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                />
                <MapController
                  selectedCountryCode={selectedCountryCode || null}
                  selectedRegion={regionFilter || null}
                  showRegionsMode={showRegions}
                  onBoundsChange={handleBoundsChange}
                  countryLeads={countryLeads}
                />

                {/* Region selection mode - show all clickable polygons */}
                {showRegions && Object.entries(REGIONS).map(([key, { name, polygon }]) => (
                  <Polygon
                    key={key}
                    positions={polygon}
                    eventHandlers={{
                      click: () => {
                        setRegionFilter(key)
                        setShowRegions(false)
                      },
                      mouseover: (e) => {
                        e.target.setStyle({ fillOpacity: 0.35, weight: 3 })
                      },
                      mouseout: (e) => {
                        e.target.setStyle({ fillOpacity: 0.15, weight: 2 })
                      },
                    }}
                    pathOptions={{
                      color: '#3b82f6',
                      weight: 2,
                      fillColor: '#3b82f6',
                      fillOpacity: 0.15,
                    }}
                  >
                    <LeafletTooltip sticky direction="center" className="region-tooltip">
                      <span className="font-medium">{name}</span>
                    </LeafletTooltip>
                  </Polygon>
                ))}

                {/* Selected region boundary polygon */}
                {!showRegions && regionFilter && REGIONS[regionFilter] && (
                  <Polygon
                    positions={REGIONS[regionFilter].polygon}
                    pathOptions={{
                      color: '#3b82f6',
                      weight: 2,
                      fillColor: '#3b82f6',
                      fillOpacity: 0.08,
                      dashArray: '6, 4',
                    }}
                  />
                )}

                {/* Only show markers when not in region selection mode - limit to 1000 for performance */}
                {!showRegions && <MarkerClusterGroup
                  chunkedLoading
                  iconCreateFunction={createClusterCustomIcon}
                  maxClusterRadius={60}
                  spiderfyOnMaxZoom={true}
                  showCoverageOnHover={false}
                >
                  {filteredLeads.slice(0, 1000).map((lead) => (
                      <LeafletMarker
                        key={lead.id}
                        position={[lead.latitude!, lead.longitude!]}
                        icon={createShipyardIcon(lead.status, shortlistedIds.has(lead.id))}
                      >
                        <Popup className="shipyard-popup" maxWidth={300}>
                          <div className="p-1">
                            {/* Popup Header with Image */}
                            {lead.image_url && (
                              <div className="w-full h-24 -mx-1 -mt-1 mb-2 overflow-hidden rounded-t">
                                <img
                                  src={lead.image_url}
                                  alt={lead.name}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            )}

                            {/* Shipyard Name & Location */}
                            <h3 className="font-bold text-secondary-900 text-sm leading-tight">
                              {lead.name}
                            </h3>
                            <p className="text-xs text-secondary-500 mt-0.5 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {lead.city ? `${lead.city}, ` : ''}{lead.country}
                            </p>

                            {/* Status Badge */}
                            <div className="flex items-center gap-2 mt-2">
                              <span
                                className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: STATUS_COLORS[lead.status] }}
                              >
                                {STATUS_LABELS[lead.status]}
                              </span>
                              {lead.market_share && (
                                <span className="text-xs text-secondary-500">
                                  {lead.market_share.toFixed(1)}% market
                                </span>
                              )}
                            </div>

                            {/* Services */}
                            {lead.services && lead.services.filter(Boolean).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {lead.services.filter(Boolean).slice(0, 3).map((service, idx) => (
                                  <span
                                    key={`${service}-${idx}`}
                                    className="px-1.5 py-0.5 bg-secondary-100 text-secondary-600 rounded text-[10px]"
                                  >
                                    {service}
                                  </span>
                                ))}
                                {lead.services.filter(Boolean).length > 3 && (
                                  <span className="text-[10px] text-secondary-400">
                                    +{lead.services.filter(Boolean).length - 3} more
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-2 mt-3">
                              <button
                                onClick={(e) => toggleShortlist(e, lead.id)}
                                className={cn(
                                  'flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded transition-colors',
                                  shortlistedIds.has(lead.id)
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-purple-500 text-white hover:bg-purple-600'
                                )}
                              >
                                <Star
                                  className={cn(
                                    'w-3 h-3',
                                    shortlistedIds.has(lead.id) && 'fill-purple-500'
                                  )}
                                />
                                {shortlistedIds.has(lead.id) ? 'Listed' : 'Shortlist'}
                              </button>
                              <button
                                onClick={() => handleLeadClick(lead)}
                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium rounded transition-colors"
                              >
                                View Details
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </Popup>
                      </LeafletMarker>
                    ))}
                </MarkerClusterGroup>}
              </MapContainer>

              {/* Map Legend - Top 10 countries */}
              <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 max-h-64 overflow-y-auto">
                <h4 className="text-xs font-semibold text-secondary-700 mb-2">Countries</h4>
                <div className="space-y-1.5">
                  {filteredCountryStats.slice(0, 10).map((country, idx) => (
                    <button
                      key={`legend-${idx}`}
                      onClick={() => setSelectedCountryCode(country.country)}
                      className={cn(
                        'flex items-center justify-between w-full text-left px-2 py-1 rounded text-sm transition-colors',
                        selectedCountry?.country === country.country
                          ? 'bg-primary-100 text-primary-800'
                          : 'hover:bg-secondary-100'
                      )}
                    >
                      <span className="truncate">{country.country}</span>
                      <span className="text-secondary-500 font-medium ml-2">{country.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Side Panel - Region Selection or Market Share */}
            {showRegions ? (
              <div className="w-80 bg-white border-l border-secondary-200 overflow-y-auto">
                <div className="p-4 border-b border-secondary-200">
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary-600" />
                    <h3 className="font-semibold text-secondary-900">Select Region</h3>
                  </div>
                  <p className="text-sm text-secondary-500 mt-1">
                    Click on a region on the map or select below
                  </p>
                </div>
                <div className="p-4">
                  <div className="space-y-1">
                    {Object.entries(REGIONS).map(([key, { name }]) => (
                      <button
                        key={key}
                        onClick={() => {
                          setRegionFilter(key)
                          setShowRegions(false)
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : pieData.length > 0 && (
              <div className="w-80 bg-white border-l border-secondary-200 overflow-y-auto">
                <div className="p-4 border-b border-secondary-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-secondary-900">
                      {regionFilter && REGIONS[regionFilter]
                        ? REGIONS[regionFilter].name
                        : selectedCountry ? selectedCountry.country : 'Visible Area'}
                    </h3>
                    {(selectedCountry || regionFilter) && (
                      <button
                        onClick={() => {
                          if (regionFilter) setRegionFilter('')
                          if (selectedCountry) setSelectedCountryCode('')
                        }}
                        className="p-1 text-secondary-400 hover:text-secondary-600 rounded"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-secondary-600 mt-1">
                    {filteredLeads.length} companies in view
                    {loadingMapLeads && <span className="text-secondary-400"> ...</span>}
                  </p>
                </div>

                {/* Pie Chart - Revenue */}
                <div className="p-4 border-b border-secondary-200">
                  <h4 className="text-sm font-medium text-secondary-700 mb-3">Revenue (Top 8)</h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                          onClick={(data) => navigate(`/admin/leads/${data.id}`)}
                          style={{ cursor: 'pointer' }}
                          isAnimationActive={false}
                        >
                          {pieData.map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={PIE_COLORS[index % PIE_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => [formatRevenue(Number(value)), 'Revenue']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Legend */}
                  <div className="space-y-1 mt-3">
                    {pieData.map((entry, index) => (
                      <button
                        key={`pie-${index}`}
                        onClick={() => navigate(`/admin/leads/${entry.id}`)}
                        className="flex items-center gap-2 w-full text-left px-2 py-1 rounded hover:bg-secondary-50 transition-colors"
                      >
                        <span
                          className="w-3 h-3 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                        />
                        <span className="text-xs text-secondary-700 truncate flex-1">
                          {entry.name}
                          {entry.countryCode && (
                            <span className="ml-1 text-secondary-400">({entry.countryCode})</span>
                          )}
                        </span>
                        <span className="text-xs text-secondary-500 font-medium">
                          {formatRevenue(entry.value)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Shipyard List */}
                <div className="p-4">
                  <h4 className="text-sm font-medium text-secondary-700 mb-3">
                    Companies ({filteredLeads.length})
                  </h4>
                  <div className="space-y-2">
                    {filteredLeads.slice(0, 20).map((lead) => (
                      <button
                        key={lead.id}
                        onClick={() => handleLeadClick(lead)}
                        className="w-full text-left p-3 rounded-lg border border-secondary-200 hover:border-primary-300 hover:bg-primary-50/50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h5 className="font-medium text-secondary-900 text-sm truncate">
                              {lead.name}
                            </h5>
                            {lead.city && (
                              <p className="text-xs text-secondary-500 flex items-center gap-1 mt-0.5">
                                <MapPin className="w-3 h-3" />
                                {lead.city}
                              </p>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: STATUS_COLORS[lead.status] }}
                          >
                            {STATUS_LABELS[lead.status]}
                          </span>
                          {lead.type && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-600">
                              {lead.type}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* List View */
          <div className="h-full overflow-auto p-4 sm:p-6">
            {/* Active Filters Summary */}
            {hasActiveFilters && !showFilters && (
              <div className="mb-4 flex items-center gap-2 text-sm">
                <span className="text-secondary-500">
                  {filteredLeads.length} results
                </span>
                <span className="text-secondary-300">|</span>
                <button
                  onClick={() => setShowFilters(true)}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
                </button>
                <button
                  onClick={clearFilters}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Mobile: Compact List */}
            <div className="sm:hidden space-y-2">
              {filteredLeads.slice(0, 200).map((lead) => (
                <div
                  key={lead.id}
                  onClick={(e) => handleLeadClick(lead, e)}
                  onAuxClick={(e) => handleLeadClick(lead, e)}
                  className="w-full text-left p-3 rounded-lg border border-secondary-200 bg-white hover:border-primary-300 hover:bg-primary-50/30 transition-all flex items-center gap-3 cursor-pointer"
                >
                  {/* Small Image/Icon */}
                  <div className="w-12 h-12 bg-secondary-100 rounded-lg flex-shrink-0 overflow-hidden flex items-center justify-center">
                    {lead.image_url ? (
                      <img
                        src={lead.image_url}
                        alt={lead.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Anchor className="w-6 h-6 text-secondary-300" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-secondary-900 text-sm truncate">{lead.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-secondary-500 mt-0.5">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">
                        {lead.city ? `${lead.city}, ` : ''}
                        {lead.country}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-medium text-white"
                        style={{ backgroundColor: STATUS_COLORS[lead.status] }}
                      >
                        {STATUS_LABELS[lead.status]}
                      </span>
                      {lead.market_share && (
                        <span className="text-[10px] text-secondary-500">
                          {lead.market_share.toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Shortlist Button */}
                  <button
                    onClick={(e) => toggleShortlist(e, lead.id)}
                    className="p-1.5 hover:bg-purple-50 rounded-lg transition-colors flex-shrink-0"
                  >
                    <Star
                      className={cn(
                        'w-4 h-4 transition-colors',
                        shortlistedIds.has(lead.id)
                          ? 'text-purple-500 fill-purple-500'
                          : 'text-secondary-300 hover:text-purple-500'
                      )}
                    />
                  </button>

                  <ChevronRight className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                </div>
              ))}
            </div>

            {/* Desktop: Grid Cards */}
            <div className="hidden sm:grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredLeads.slice(0, 200).map((lead) => (
                <div
                  key={lead.id}
                  onClick={(e) => handleLeadClick(lead, e)}
                  onAuxClick={(e) => handleLeadClick(lead, e)}
                  className="text-left p-4 rounded-xl border border-secondary-200 bg-white hover:border-primary-300 hover:shadow-md transition-all cursor-pointer relative group"
                >
                  {/* Top Action Buttons */}
                  <div className="absolute top-2 right-2 flex items-center gap-1 z-10">
                    {/* Open in New Tab */}
                    <button
                      onClick={(e) => openInNewTab(e, lead)}
                      className="p-1.5 bg-white/90 hover:bg-secondary-100 rounded-lg shadow-sm transition-colors opacity-0 group-hover:opacity-100"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-4 h-4 text-secondary-500" />
                    </button>
                    {/* Shortlist Button */}
                    <button
                      onClick={(e) => toggleShortlist(e, lead.id)}
                      className="p-1.5 bg-white/90 hover:bg-purple-50 rounded-lg shadow-sm transition-colors"
                    >
                      <Star
                        className={cn(
                          'w-4 h-4 transition-colors',
                          shortlistedIds.has(lead.id)
                            ? 'text-purple-500 fill-purple-500'
                            : 'text-secondary-400 hover:text-purple-500'
                        )}
                      />
                    </button>
                  </div>

                  {/* Image */}
                  <div className="aspect-video bg-secondary-100 rounded-lg mb-3 overflow-hidden">
                    {lead.image_url ? (
                      <img
                        src={lead.image_url}
                        alt={lead.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Anchor className="w-10 h-10 text-secondary-300" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <h3 className="font-semibold text-secondary-900 truncate">{lead.name}</h3>

                  <div className="flex items-center gap-1 text-sm text-secondary-500 mt-1">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="truncate">
                      {lead.city ? `${lead.city}, ` : ''}
                      {lead.country}
                    </span>
                  </div>

                  {/* Services */}
                  {lead.services && lead.services.filter(Boolean).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {lead.services.filter(Boolean).slice(0, 2).map((service, idx) => (
                        <span
                          key={`${service}-${idx}`}
                          className="px-2 py-0.5 bg-secondary-100 text-secondary-600 rounded text-xs truncate max-w-[120px]"
                        >
                          {service}
                        </span>
                      ))}
                      {lead.services.filter(Boolean).length > 2 && (
                        <span className="px-2 py-0.5 bg-secondary-100 text-secondary-500 rounded text-xs">
                          +{lead.services.filter(Boolean).length - 2}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-secondary-100">
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: STATUS_COLORS[lead.status] }}
                    >
                      {STATUS_LABELS[lead.status]}
                    </span>
                    {lead.market_share && (
                      <span className="text-xs text-secondary-500">
                        {lead.market_share.toFixed(1)}% market
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {filteredLeads.length === 0 && (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-secondary-900">No companies found</h3>
                <p className="text-secondary-600 mt-1">Try adjusting your filters</p>
              </div>
            )}

            {filteredLeads.length > 200 && (
              <div className="mt-6 text-center py-4 bg-secondary-100 rounded-lg">
                <p className="text-secondary-600">
                  Showing 200 of {filteredLeads.length.toLocaleString()} results.{' '}
                  <button
                    onClick={() => setShowFilters(true)}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Use filters
                  </button>
                  {' '}to narrow down.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
